import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, RefreshCw, ArrowLeft } from "lucide-react";
import { resendVerification } from "../../api/auth.api";

const RESEND_COOLDOWN_S = 30;

export default function CheckYourEmail() {
  const { state } = useLocation();
  const email = state?.email || "";
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  // Cooldown timer (seconds remaining) — prevents the user from spamming
  // resends in a panic. Backend also rate-limits but we want a friendly
  // local UI signal so they know to wait instead of clicking blindly.
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const onResend = async () => {
    if (!email) {
      setErr("No email on file. Please sign up again.");
      return;
    }
    if (cooldown > 0) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await resendVerification(email);
      setMsg("Verification email sent. Please check your inbox and spam folder.");
      setCooldown(RESEND_COOLDOWN_S);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      if (status === 429) {
        setErr("Too many requests — please wait a minute before trying again.");
        setCooldown(60);
      } else {
        setErr(detail || "Could not resend. Try again in a minute.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-main text-text-primary flex flex-col items-center justify-center px-6 py-10">
      <Link to="/" className="font-bold text-xl tracking-tight mb-8">
        Zen<span className="text-button-primary">cricket</span>data
      </Link>

      <div className="w-full max-w-md bg-bg-primary border border-border-default rounded-2xl p-8 sm:p-10 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-button-primary/10 flex items-center justify-center mb-5">
          <Mail className="w-7 h-7 text-button-primary" />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight mb-2">Check your email</h1>
        <p className="text-text-secondary text-sm">
          We sent a verification link to{" "}
          <span className="text-text-primary font-medium">{email || "your inbox"}</span>.
          Click it to activate your account.
        </p>

        {msg && <div className="mt-5 px-3.5 py-2.5 rounded-lg text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">{msg}</div>}
        {err && <div className="mt-5 px-3.5 py-2.5 rounded-lg text-sm bg-button-primary/10 border border-button-primary/30 text-button-primary">{err}</div>}

        <button
          onClick={onResend}
          disabled={busy || cooldown > 0}
          title={cooldown > 0 ? `Wait ${cooldown}s before resending` : ""}
          className="mt-6 w-full bg-button-primary hover:brightness-110 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
        >
          <RefreshCw className={`w-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Resending…" :
           cooldown > 0 ? `Resend in ${cooldown}s` :
           "Resend verification email"}
        </button>

        <p className="mt-3 text-[11px] text-text-secondary">
          Check your spam folder if you don't see it within 30 seconds.
        </p>

        <Link
          to="/login"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
